/**
 * The change in a position that wraps every `copyWidth`, taken the short way round — so the jump a
 * wrap makes reads as no movement at all rather than as a copy-width of travel.
 */
export function wrappedDelta(delta: number, copyWidth: number): number {
  if (copyWidth <= 0) return delta;
  if (delta > copyWidth / 2) return delta - copyWidth;
  if (delta < -copyWidth / 2) return delta + copyWidth;
  return delta;
}

/**
 * Whether the card in the middle is within `loadAhead` cards of the last one loaded — the point
 * where a swipe towards the end is about to wrap round onto games already seen. Travel alone can't
 * see this: it resets whenever a page lands, whatever position the player is at.
 */
export function isNearEnd(index: number, gameCount: number, loadAhead: number): boolean {
  return gameCount > 0 && index >= gameCount - loadAhead;
}

/**
 * Whether the player has travelled far enough through the loaded games to need the next page.
 *
 * A looping row has no end to scroll towards, and the games appended by a page land behind the seam
 * rather than ahead of the player, so where the focal card sits says nothing about how much
 * unseen runway is left. How far the row has moved since the list last grew does: once it has
 * covered all but `loadAhead` cards, the next lap is about to repeat what has already been seen.
 */
export function needsNextPage({
  travelled,
  stride,
  gameCount,
  loadAhead,
}: {
  travelled: number;
  stride: number;
  gameCount: number;
  loadAhead: number;
}): boolean {
  if (stride <= 0) return false;
  return travelled >= stride * Math.max(1, gameCount - loadAhead);
}
